import { parseQuoteNumberText } from "@/components/quote/quoteNumberText";
/**
 * 装配体项目报价 · 展示规则(纯函数,无 React、无网络)。
 *
 * 放这里的都是**会算错钱的判断**,所以单独成模块、单独测:
 *   · 什么时候**不许**显示总价(方案 §7.3 的硬规则)
 *   · 两档单价怎么从后端 quote_sheet 里取到同一行上
 *   · 清单概览那几个数怎么念
 *
 * 铁律:这里**不做任何金额算术**。合计、套价、综合利润率一律取后端 `totals`。
 * 本模块只决定「显不显示、怎么念」。
 */
import type {
  AddDrawingFilesResult,
  DrawingFileOutcome,
  EffectiveParam,
  InferredPick,
  InferredProcess,
  LineInference,
  LoosePairing,
  NotPricedLine,
  ProcessReview,
  ProjectCalibration,
  ProjectChecklistSummary,
  ProjectSimilar,
  ProjectLine,
  ProjectSummary,
  ProjectTotalsBlock,
} from "@/api/quoteProjects";
import { QUOTABLE_KINDS } from "@/api/quoteProjects";
import {
  PROJECT_LINE_BADGE_TIER_META,
  PROJECT_LINE_BADGE_TIERS,
  type ProjectLineBadgeKey,
} from "@/components/quote/quoteVocabulary";
import { toleranceTierLabel as toleranceLabel } from "@/components/quote/toleranceTier";

/** 总价的展示状态。`hidden` 时界面显示「——」,**不显示部分合计**。 */
export type TotalDisplay =
  | { kind: "ready"; totals: ProjectTotalsBlock }
  | { kind: "hidden"; reason: string; notPriced: NotPricedLine[] };

/**
 * 方案 §7.3 硬规则:**预填没完成就不显示总价**,显示「——」。
 *
 * 为什么不显示「部分合计」:有人会把那个半截数字直接报给客户。少一行零件
 * 可能就是几千块。宁可让人等,也不给一个看起来像总价的数。
 */
export function totalDisplay(
  summary: ProjectSummary | null,
  sets: number,
): TotalDisplay {
  if (summary == null) {
    return { kind: "hidden", reason: "还没算", notPriced: [] };
  }
  const totals = summary.totals[String(sets)];
  if (totals == null) {
    return {
      kind: "hidden",
      reason: `没有 ${sets} 套这一档的结果`,
      notPriced: [],
    };
  }
  if (summary.partial) {
    const n = summary.not_priced.length;
    return {
      kind: "hidden",
      reason: `还有 ${n} 行没算进来,总价不完整`,
      notPriced: summary.not_priced,
    };
  }
  return { kind: "ready", totals };
}

/** 「开始报价」以后后台逐行预填的进度(只数勾选了、建了草稿的行)。 */
export interface PrefillProgress {
  /** 建了草稿的行 */
  total: number;
  /** 算完了的(含失败) */
  done: number;
  /** AI 识别完成的行(prefill_state = ready) */
  ready: number;
  /**
   * 已出价:识别完成、而且后端明说不缺必填(`missing_inputs` 是空数组)的行。
   * 不看 quote_id —— 有草稿不等于有价;旧后端不给缺项时不知道有没有价,不算。
   */
  priced: number;
  failed: number;
  /** 还在排队或在算 —— 大于 0 时页面定时刷新 */
  active: number;
}

/**
 * 这一行识别失败了、而且后端下次「开始报价 / 重试」会碰它:勾选的要计价行。
 * 进度、步骤待处理、「重试失败的 N 行」、「识别失败」筛选都用这一条(复审:筛选原来没看勾选,
 * 排除的失败行也算进去,与进度和按钮的数对不上;后端对排除的行直接跳过)。
 */
export function lineRecognitionFailed(ln: ProjectLine): boolean {
  return (
    QUOTABLE_KINDS.has(ln.kind) &&
    ln.included &&
    ln.prefill_state === "failed" &&
    // 人取消了的不算识别失败(阶段三 S3-5 · 决定 D3-15):后端开始报价 / 重试也不带它,要逐行「继续算」
    ln.prefill_reason !== "cancelled"
  );
}

/**
 * 已出价:识别完成、而且后端明说不缺必填(`missing_inputs` 是空数组)。不看 quote_id ——
 * 有草稿不等于有价;旧后端不给缺项时不知道有没有价,不算。进度「已出价」和步骤「总价未完整」共用。
 */
export function linePriced(ln: ProjectLine): boolean {
  return (
    ln.prefill_state === "ready" &&
    Array.isArray(ln.missing_inputs) &&
    ln.missing_inputs.length === 0
  );
}

export function prefillProgress(lines: ProjectLine[]): PrefillProgress {
  const drafted = lines.filter(
    (l) => l.included && l.quote_id != null && l.prefill_state != null,
  );
  const ready = drafted.filter((l) => l.prefill_state === "ready");
  const failed = drafted.filter(lineRecognitionFailed).length;
  const active = drafted.filter(
    (l) => l.prefill_state === "pending" || l.prefill_state === "running",
  ).length;
  return {
    total: drafted.length,
    done: drafted.length - active,
    ready: ready.length,
    priced: ready.filter(linePriced).length,
    failed,
    active,
  };
}

/**
 * 页面顶上那一句(总纲 §5.5 P3 / §5.9 R7):识别完成和已出价分开数 —— 识别完了不等于有价。
 * 还在算、或有失败的行时说;全部算完、也没有失败的行时不说话。
 */
export function prefillProgressText(p: PrefillProgress): string | null {
  if (p.active === 0 && p.failed === 0) return null;
  const head = `AI 识别完成 ${p.ready}/${p.total} 行 · 已出价 ${p.priced} 行`;
  return p.failed > 0 ? `${head} · 识别失败 ${p.failed} 行` : head;
}

/**
 * 「开始报价」按钮可以改叫「重试失败的 N 行」的条件:要计价的勾选行**全都有草稿**(这次不会新建、
 * 不扣额度)、没有在排队 / 在算的行、**没有补了 3D 还没按它算的行**、有失败行。同一个接口、同一个请求体。
 *
 * ⚠ 同一次请求不只重试失败行(`_refresh_reused_draft`):换过图纸、补了 3D、选型有更新、一次性费用
 * 收法变了的已有草稿也会一起重算。看得见的那一种(行字段 `cad_pending`,与后端是同一条判定)出现时
 * 不改名 —— 页面上「再点「开始报价」按 3D 重算」的说明才和按钮对得上;看不全的几种由按钮旁的
 * `RETRY_ALSO_RERUNS` 说出来。条件不满足返回 0,按钮照原样写。
 */
export function retryFailedCount(lines: readonly ProjectLine[]): number {
  const live = lines.filter((l) => QUOTABLE_KINDS.has(l.kind) && l.included);
  if (live.length === 0 || live.some((l) => l.quote_id == null)) return 0;
  if (
    live.some(
      (l) =>
        l.prefill_state === "pending" ||
        l.prefill_state === "running" ||
        l.cad_pending === true,
    )
  )
    return 0;
  return live.filter(lineRecognitionFailed).length;
}

/** 按钮写「重试失败的 N 行」时旁边那一句:同一次请求还会重算前端看不全的那几种行。 */
export const RETRY_ALSO_RERUNS =
  "其他换过图纸、选型有更新的行也会一起重算，人改过的值不动。";

/** 说明文字里引用开始按钮时用的名字(不带行数的「开始报价」/ 带行数的重试名)。 */
export function startActionName(retry: number): string {
  return retry > 0 ? `重试失败的 ${retry} 行` : "开始报价";
}

/** F10:从超时行带到第 1 步时,要写下一步说明的那一行。 */
export interface PrefillGuideTarget {
  name: string;
  /** upload = 换一份清楚的图纸;unbind = 只用 3D 报(解除 / 清空页码) */
  target: "upload" | "unbind";
  /** 这一行的图纸页码;null = 整份挂上的图纸(只能「解除」) */
  drawingPage: number | null;
  /** 图纸是人工配的(`drawing_match === "人工"`):后端补传不会替换它 */
  manualDrawing: boolean;
}

/**
 * 下一步说明。**渲染时**按当前的重试数生成 —— 点下去之后上传图纸、新建行、补 3D 都可能让按钮换名,
 * 定死在点击那一刻的文字会指向一个已经不存在的按钮名。
 */
export function prefillGuideText(g: PrefillGuideTarget, retry: number): string {
  const then = `点「${startActionName(retry)}」`;
  if (g.target === "upload")
    return g.manualDrawing
      ? `「${g.name}」的图纸是人工配的，新图纸不会自动替换：上传后在下面把它整份配给「${g.name}」，再${then}。`
      : `上传一份更清楚的图纸配到「${g.name}」，再${then}。`;
  return g.drawingPage == null
    ? `点「${g.name}」的「解除」后，${then}就只按 3D 重新识别。`
    : `清空「${g.name}」的图纸页码后，${then}就只按 3D 重新识别。`;
}

/** 一次最多算几档(后端汇总接口 `tiers` 的上限)。 */
export const MAX_TIERS = 4;

/**
 * 「数量档(套)」输入框里的字 → 档位:只认完整正十进制安全整数,整组合法才去重排序。
 *
 * 只在离开输入框、点「算价」时调一次 —— 每敲一个键就解析回写,刚敲的逗号会被吞掉
 * (「1,」解析成 [1] 再写回成「1」,接着敲 10 就成了 110)。
 */
export function parseTiers(text: string): number[] {
  const groups = text.trim().split(/[,，、]/);
  if (groups.some((group) => group.trim() === "")) return [];
  const parts = groups.flatMap((group) => group.trim().split(/\s+/));
  const values = parts.map((part) =>
    parseQuoteNumberText(part, "positive-integer"),
  );
  if (values.some((value) => value === null)) return [];
  return [...new Set(values as number[])].sort((a, b) => a - b);
}

/**
 * 算价、导出用的档位:**一定带上当前套数**。总价按套数那一档显示 —— 档位里没有它,
 * 页面只能写「——」,导出的也是别的套数的价。并进去超过上限时返回 null(让人删一档)。
 */
export function tiersWithSets(tiers: number[], sets: number): number[] | null {
  const all = [...new Set([...tiers, sets])].sort((a, b) => a - b);
  return all.length > MAX_TIERS ? null : all;
}

/** 一行零件在报价单上的展示(各档单价落在同一行)。 */
export interface SheetRow {
  index: number;
  name: string;
  /** 套数档 → 该档的单价 / 件数 / 小计。键是档位数字。 */
  byTier: Record<
    number,
    { unitPrice: number; quantity: number; subtotal: number }
  >;
}

/**
 * 后端 `quote_sheet` 的列名是 `报价(N 套)` 这种动态键 —— 解析成结构化行。
 *
 * **按档位逐个取键,不靠列顺序**:按顺序取的话后端加一档、换一档就会错位,
 * 而错位的表现是「把 A 件的批量价印到 B 件那一行」,看起来完全正常。
 */
export function parseSheet(summary: ProjectSummary): SheetRow[] {
  return summary.quote_sheet.map((row, i) => {
    const byTier: SheetRow["byTier"] = {};
    for (const t of summary.tiers) {
      const unit = row[`报价(${t} 套)`];
      const qty = row[`数量(${t} 套)`];
      const sub = row[`小计(${t} 套)`];
      if (typeof unit !== "number") continue;
      byTier[t] = {
        unitPrice: unit,
        quantity: typeof qty === "number" ? qty : 0,
        subtotal: typeof sub === "number" ? sub : 0,
      };
    }
    return {
      index: typeof row["编号"] === "number" ? row["编号"] : i + 1,
      name: String(row["零件名称"] ?? ""),
      byTier,
    };
  });
}

/** 清单概览一句话。件数说的是**加工件**,标准件/外购件只列清单不计价(D1)。 */
export function checklistCaption(
  s: ProjectChecklistSummary,
  sets: number,
): string {
  const parts = [
    s.sheet_metal > 0
      ? `自制件 ${s.made} 种 + 钣金件 ${s.sheet_metal} 种 / ${s.quotable_pieces} 件`
      : `自制件 ${s.made} 种 / ${s.quotable_pieces} 件`,
  ];
  if (s.standard > 0) parts.push(`标准件 ${s.standard} 种(不计价)`);
  if (s.purchased > 0) parts.push(`外购件 ${s.purchased} 种(不计价)`);
  if (s.unknown > 0) parts.push(`待归类 ${s.unknown} 种`);
  if (s.unsupported > 0) parts.push(`需人工报价 ${s.unsupported} 种(不计价)`);
  const suffix = sets > 1 ? ` · 共 ${sets} 套` : "";
  return parts.join(" · ") + suffix;
}

/** 能不能开始报价:一个计价的零件(自制件 / 钣金件)都没有就没得报。 */
export function canStartPricing(s: ProjectChecklistSummary): {
  ok: boolean;
  why: string;
} {
  if (s.quotable_lines <= 0) {
    return {
      ok: false,
      why: "清单里没有要计价的零件(自制件 / 钣金件)—— 标准件、外购件、需人工报价的不计价",
    };
  }
  return { ok: true, why: "" };
}

/** 工作台的行状态徽章。顺序即优先级:先说最挡事的。 */
/**
 * 图纸是按近似名自动配上的、人还没确认(后端没确认前不拿这页报价)。
 * 人在页码栏确认或改配后 drawing_match 变成「人工」。
 */
export function drawingNeedsConfirm(ln: ProjectLine): boolean {
  return ln.drawing_page != null && (ln.drawing_match ?? "").startsWith("模糊");
}

/**
 * 草稿预填完了还缺必填输入的行(徽章「缺 …」、统计「缺必填」与工作台筛选共用这一条)。
 * 2026-10-19 验收:4 行都缺材料,工作台却写「需确认 0」、状态列空着,要点开一行往下翻才知道。
 */
export function lacksInputs(ln: ProjectLine): boolean {
  return (ln.missing_inputs?.length ?? 0) > 0;
}

/** 行上的「缺 …」徽章:最多列 3 样,多的写「等 N 项」 */
export function missingBadge(ln: ProjectLine): string | null {
  const items = ln.missing_inputs ?? [];
  if (items.length === 0) return null;
  const shown = items.slice(0, 3).join("、");
  return items.length > 3 ? `缺${shown}等 ${items.length} 项` : `缺${shown}`;
}

/** 要计价、却还没建草稿的行(徽章「未建草稿」与工作台筛选共用这一条)。 */
export function lacksDraft(ln: ProjectLine): boolean {
  return QUOTABLE_KINDS.has(ln.kind) && ln.quote_id == null;
}

/**
 * 这一行有没有图纸:配了页码,或整份 PDF 就是这件的图(零散多件、补传按文件名配上的 —— 没有页码)。
 * 只看页码的话,整份挂上图纸的行会被说成「无图纸」、也不算进「已配上几个」。
 */
export function hasDrawing(
  ln: Partial<Pick<ProjectLine, "drawing_page" | "drawing_document_id">>,
): boolean {
  return ln.drawing_page != null || ln.drawing_document_id != null;
}

/**
 * 要计价、却没有 3D 模型的行(只有图纸建的项目、零散多件里只有图的行 …):尺寸、重量、工时只能读图估,
 * 精度比有 3D 时差;工艺按读图判断预填、没有 3D 核对过。后端明说 `has_3d === false` 才算 —— 旧后端不给这一项,不乱标。
 */
export function lacks3d(ln: ProjectLine): boolean {
  return QUOTABLE_KINDS.has(ln.kind) && ln.has_3d === false;
}

/** 行徽章:没有 3D(悬停看这意味着什么)。 */
export const NO_3D_BADGE = "无 3D · 读图估";
/** 行徽章:补传了 3D、价不是按它算的。 */
export const CAD_PENDING_BADGE = "3D 已补 · 价没按它算";

/** 补了 3D 以后怎么让价跟上 —— 「3D」一格的悬停说明和页头用这一句。 */
export const CAD_PENDING_WHY =
  "报价还是草稿的话,再点「开始报价」按 3D 重算(人改过的值不动);已经送审或批准的报价不会重算。";

/** 补传的 3D 里没数到实体(后端记 `cad_bodies = 0`)—— 「3D」一格的悬停说明。 */
export const CAD_NO_SOLID_WHY =
  "这份 3D 转换成功了,里面却没数到实体(多半只有曲面):尺寸、重量、工时可能读不出来或不准。最好换成实体模型再传。";

/** 没有 3D 意味着什么 —— 清单上方的说明、徽章的悬停说明、补传入口旁边都用这一句。 */
export const NO_3D_WHY =
  "没有 3D 模型:尺寸、重量、工时是读图估的(读不出时要手填),精度比有 3D 时差;" +
  "工艺是按读图判断预填的,没有 3D 核对过,请核对。可以在「确认零件清单」里逐行补传 3D。";

/**
 * 清单上方的一句话:几行没有 3D、这意味着什么、怎么办。没有这样的行(或旧后端)→ null。
 * 只数要计价、勾选了的行。补了 3D 还没重算的行另说一句。
 */
export function no3dNote(lines: readonly ProjectLine[]): string | null {
  const live = lines.filter((l) => l.included);
  const missing = live.filter(lacks3d).length;
  const pending = live.filter((l) => l.cad_pending).length;
  if (missing === 0 && pending === 0) return null;
  const parts: string[] = [];
  if (missing > 0) parts.push(`${missing} 行${NO_3D_WHY}`);
  if (pending > 0)
    parts.push(
      `${pending} 行补传了 3D,价还是按原来的图纸估的 —— ${CAD_PENDING_WHY}`,
    );
  return parts.join(" ");
}

/**
 * 清单上方那句的短结论(R1 默认可见一段 ≤ 40 字):几行没有 3D / 几行补了 3D 待重算、请核对。
 * 整段说明(`no3dNote`)收进旁边的「说明」里;没有这样的行 → null(和 `no3dNote` 同一判据)。
 */
export function no3dBrief(lines: readonly ProjectLine[]): string | null {
  const live = lines.filter((l) => l.included);
  const missing = live.filter(lacks3d).length;
  const pending = live.filter((l) => l.cad_pending).length;
  if (missing === 0 && pending === 0) return null;
  const parts: string[] = [];
  if (missing > 0) parts.push(`${missing} 行没有 3D，尺寸工时按读图估`);
  if (pending > 0) parts.push(`${pending} 行补了 3D 待重算`);
  return `${parts.join("；")}，请核对`;
}

/** 行上的一个提醒:键(决定分级,见词表 `PROJECT_LINE_BADGE_TIERS`)+ 给人看的字 + 悬停说明。 */
export interface LineBadge {
  key: ProjectLineBadgeKey;
  text: string;
  why?: string;
}

/**
 * 行上的全部提醒(顺序 = 原来的显示顺序)。给了标定结果就把「外推 / 样本少」也并进来,
 * 和其它提醒一起分级、一起受「一行最多露 2 个」管。
 */
export function lineBadgeItems(
  ln: ProjectLine,
  calibration?: ProjectCalibration | null,
): LineBadge[] {
  const out: LineBadge[] = [];
  if (!ln.included) out.push({ key: "excluded", text: "已排除" });
  if (ln.needs_review) out.push({ key: "needs_review", text: "需确认" });
  if (ln.kind_overridden)
    out.push({ key: "kind_overridden", text: "人工改过分类" });
  if (lacksDraft(ln)) out.push({ key: "no_draft", text: "未建草稿" });
  const missing = missingBadge(ln);
  if (missing) out.push({ key: "missing_inputs", text: missing });
  if (lacks3d(ln))
    out.push({ key: "no_3d", text: NO_3D_BADGE, why: NO_3D_WHY });
  if (ln.cad_pending)
    out.push({
      key: "cad_pending",
      text: CAD_PENDING_BADGE,
      why: CAD_PENDING_WHY,
    });
  if (!hasDrawing(ln) && QUOTABLE_KINDS.has(ln.kind))
    out.push({ key: "no_drawing", text: "无图纸" });
  if (drawingNeedsConfirm(ln))
    out.push({ key: "drawing_match", text: "图纸按近似名配上 · 请核对" });
  const cal = calibrationChip(ln.id, calibration);
  if (cal)
    out.push({
      key: cal.text === "外推" ? "extrapolated" : "few_samples",
      text: cal.text,
      why: cal.why,
    });
  return out;
}

export function lineBadges(ln: ProjectLine): string[] {
  return lineBadgeItems(ln).map((b) => b.text);
}

/** 按 挡出价 → 要核对 → 提示 排;同一级保持原来的先后(返回新数组)。 */
export function rankLineBadges(items: readonly LineBadge[]): LineBadge[] {
  const rank = (b: LineBadge) =>
    PROJECT_LINE_BADGE_TIER_META[PROJECT_LINE_BADGE_TIERS[b.key]].rank;
  return [...items].sort((a, b) => rank(a) - rank(b));
}

/**
 * 这一行的价有没有本厂真实价支撑 → 工作台上的小标签(计划 M6 第 6 条)。
 * 有域外外推 →「外推」;只有样本少 →「样本少」;域内 / 还没算价 → null。悬停看原因。
 */
export function calibrationChip(
  lineId: string,
  cal: ProjectCalibration | null | undefined,
): { text: string; why: string } | null {
  const line = cal?.lines.find((l) => l.line_id === lineId);
  if (!line || line.flags.length === 0) return null;
  const extrapolated = line.flags.some((f) => f.level === "extrapolated");
  return {
    text: extrapolated ? "外推" : "样本少",
    why: line.flags.map((f) => f.text).join("\n"),
  };
}

/**
 * 相似件参照的一行(计划 M6 第 5 条):最像的那件、它的价、本行按同样数量的价、差多少。
 * 金额给数字(页面按统一格式显示);差多少只在相似度高 / 中时给(后端按同一规则给 null)。
 * 悬停看为什么像、还有哪几件像。
 */
export function similarRow(x: ProjectSimilar["lines"][number]): {
  name: string;
  /** 只有丢单参照的行(没有成交件)为 null —— 这种行不进对照表 */
  qty: number | null;
  ours: number | null;
  twin: string;
  similarity: string;
  theirs: number | null;
  diff: string;
  why: string;
} {
  const best = x.cases[0];
  const others = x.cases
    .slice(1)
    .map((c) => `${c.name}(${c.similarity})`)
    .join("、");
  const pct = x.diff == null ? null : Math.abs(x.diff) * 100;
  return {
    name: x.name,
    qty: x.qty,
    ours: x.unit_price,
    twin: best ? best.name : "——",
    similarity: best ? best.similarity : "——",
    theirs: x.qty == null ? null : (best?.unit_price[String(x.qty)] ?? null),
    diff:
      pct == null
        ? "——"
        : `${x.diff! > 0 ? "+" : x.diff! < 0 ? "−" : ""}${pct.toFixed(1)}%`,
    why: [best?.why, others ? `其他相似:${others}` : ""]
      .filter(Boolean)
      .join("\n"),
  };
}

/**
 * 金额占比:把人的核对精力引到最贵的零件上(方案 §7.3)。
 *
 * 分母用**后端给的零件合计**,不在前端把各行加一遍 —— 两处求和迟早会差一分钱,
 * 而占比列对不上合计会让人怀疑整张单子。
 */
export function shareOfTotal(
  subtotal: number,
  partsTotal: number,
): number | null {
  if (!(partsTotal > 0)) return null;
  return subtotal / partsTotal;
}

/** 排序:按金额降序(方案 §7.3 默认排序)。同额按编号,保证稳定。 */
export function byAmountDesc(tier: number) {
  return (a: SheetRow, b: SheetRow): number => {
    const av = a.byTier[tier]?.subtotal ?? 0;
    const bv = b.byTier[tier]?.subtotal ?? 0;
    return bv - av || a.index - b.index;
  };
}

/* ── 图纸读到了什么(逐件推理 · 计划 M4:值 / 来源 / 依据)────────────────── */

/** 一项推理在清单里的显示。`sure=false` 显示为「需确认」,悬停看 `why`。 */
export interface InferenceChip {
  key: string;
  label: string;
  text: string;
  sure: boolean;
  why: string;
}

function pickText(p: InferredPick): string {
  return p.grade ? `${p.name_zh} ${p.grade}` : p.name_zh;
}

/** 推理结果 → 逐项显示。没有图纸(null)→ 空。不确定的项照样列出,文字写「待定」并带上原因。 */
/** 填进去的工艺为什么还要看一眼(后端 `process_review` 的原因)→ 接在「按读图填入」后面的那半句。 */
const REVIEW_SUFFIX: Record<ProcessReview, string> = {
  blocked: "· 3D 显示可能要多轴 · 请核对",
  no_3d: "· 只有图纸、没有 3D 核对",
  no_result: "· 3D 没核对出结果",
  multi_axis: "· 读图分不准是哪种多轴",
  process_class: "· 读图只分得出工艺大类",
  unchecked: "· 没有 3D 核对",
};

/**
 * 读图工艺标签的文字。
 *
 * 读图识别到的工艺**都先填进草稿、人来核对**(用户 2026-10-14 定:不管有没有 3D)。填进去的写「按读图填入」,
 * 后面接着写**为什么要看一眼** —— 没有 3D 核对过、3D 显示可能要多轴、读图分不准是哪种多轴。原因直接写在标签上:
 * 只放在悬停说明里,人看到一个已经填好的工艺多半就不看了(计划 §5.18)。
 * 「要不要看、为什么」由后端判(`ProjectLine.process_review`),这里只管把它写出来 —— 不在前端另判一遍。
 *
 * 没填进去的还有两类:工艺目录里没有那一道(读图判成车削 / 钣金时是数控车削 / 激光切割);
 * 以及 2026-10-14 之前预填的旧记录(那时 3D 核对不过就不填)。这些仍然写「请选择」。
 */
function processChipText(
  p: InferredProcess,
  byUser: boolean,
  review: ProcessReview | null | undefined,
  reviewed: boolean,
): string {
  if (byUser)
    return `${p.value ? `读图:${p.value.name_zh}` : "读图没判断出"}(已人工选定)`;
  if (!p.value) return "读图没判断出 · 请选择";
  if (p.adopted && reviewed && !review)
    return `${p.value.name_zh}(按读图填入 · 已核对)`;
  if (p.adopted)
    return `${p.value.name_zh}(按读图填入)${review ? REVIEW_SUFFIX[review] : ""}`;
  if (p.check === "blocked")
    return `读图:${p.value.name_zh} · 3D 显示可能要多轴 · 请选择`;
  if (p.check === "no_result")
    return `读图:${p.value.name_zh} · 3D 没核对出结果 · 请选择`;
  if (p.check === "no_3d")
    return `读图:${p.value.name_zh} · 只有图纸、没法用 3D 核对 · 请选择`;
  return `读图:${p.value.name_zh} · 请选择`;
}

/**
 * 工艺是按读图预填的、之后没人另定过、也没有 3D 核对通过 —— 价已经算出来了,但那个工艺没人把过关。
 * 判定在后端(`part_inference.process_review`,清单行的 `process_review`);旧后端不给这一项 = 不提。
 */
export function processNeedsReview(ln: ProjectLine): boolean {
  return ln.process_review != null;
}

/**
 * 工作台「工艺(读图)」一格显示什么。读图判断过的,就是那个标签(`inferenceChips` 的工艺一项);
 * **没有 3D、读图还没判断过、工艺也没人或项目参数定过的行,先说清楚接下来会怎样** —— 开始报价时按读图预填、
 * 没有 3D 可以核对。留一个「——」,人会以为这一行没事。有 3D 的行没判断过仍然是 null(显示「——」,和以前一样)。
 */
export function processCell(ln: ProjectLine): InferenceChip | null {
  const chip = inferenceChips(
    ln.inference,
    ln.applied,
    ln.process_review,
    ln.process_reviewed != null,
  ).find((c) => c.key === "process");
  if (chip) return chip;
  if (!lacks3d(ln) || ln.applied?.process != null) return null;
  return {
    key: "process",
    label: "工艺",
    text: "开始报价后按读图预填(没有 3D 核对,请核对)",
    sure: false,
    why: "没有 3D 模型可以核对:开始报价时工艺按读图判断预填,填的是什么、依据是什么会写在这一格 —— 请核对,不对就在工作表或批量改里换。",
  };
}

export function inferenceChips(
  inf: LineInference | null | undefined,
  applied?: Record<string, string> | null,
  /** 这一行读图填的工艺还要不要人核对、为什么(`ProjectLine.process_review`)· 不给 = 不提 */
  processReview?: ProcessReview | null,
  /** 有人对现在这个样子点过「核对无误」(`ProjectLine.process_reviewed` 有值) */
  processReviewed = false,
): InferenceChip[] {
  if (!inf) return [];
  const out: InferenceChip[] = [];
  if (inf.material) {
    const m = inf.material;
    // 人在批量改里定过材料(含目录里没有、自己填的):读图结果只作参考,不再挂「待定」催人选(和工艺同一个口径)
    const byUser = applied?.material === "user";
    out.push({
      key: "material",
      label: "材料",
      text: byUser
        ? `${m.value ? `读图:${pickText(m.value)}` : "读图没读准"}(已人工选定)`
        : m.value
          ? pickText(m.value)
          : "待定",
      sure: byUser || m.sure,
      why: m.why,
    });
  }
  if (inf.process) {
    const p = inf.process;
    // 人在工作表 / 批量改里定过工艺:读图结果只作参考,不再催人选
    const byUser = applied?.process === "user";
    out.push({
      key: "process",
      label: "工艺",
      text: processChipText(p, byUser, processReview, processReviewed),
      // 核对过的和人定的一样算「确定」:不再挂「需确认」
      sure: byUser || p.sure || (processReviewed && !processReview),
      why: p.why,
    });
  }
  (inf.surfaces?.passes ?? []).forEach((p, i) => {
    out.push({
      key: `surface-${i}`,
      label: "表处理",
      text: p.picked.length
        ? p.picked.map((x) => x.name_zh).join("、")
        : `待定(${p.text})`,
      sure: p.sure,
      why: p.why,
    });
  });
  if (inf.heat_treat) {
    const h = inf.heat_treat;
    out.push({
      key: "heat_treat",
      label: "热处理",
      text: h.value ? h.value.name_zh : "待定",
      sure: h.sure,
      why: h.why,
    });
  }
  if (inf.tolerance) {
    const t = inf.tolerance;
    out.push({
      key: "tolerance",
      label: "公差",
      text: toleranceLabel(t.value),
      sure: t.sure,
      why: t.why,
    });
  }
  if (inf.weight) {
    const w = inf.weight;
    out.push({
      key: "weight",
      label: "重量",
      text: w.value != null ? `${w.value} g` : `${w.raw}(单位待定)`,
      sure: w.sure,
      why: w.why,
    });
  }
  return out;
}

/* ── 开工前确认:生效的项目参数 + 来源(计划 §3 第 5 条 · 方案 B)──────────── */

const SOURCE_ZH: Record<string, string> = {
  project: "本项目",
  tenant: "工厂默认",
  system: "系统默认",
};
const MODE_ZH: Record<string, string> = {
  turnkey: "包工包料",
  labor_only: "纯代工",
};

/** 生效参数 → 「包工包料(工厂默认)· 利润率 30%(系统默认)· 公差 标准(系统默认)」。材料 / 工艺不在这里(逐件从图纸定)。 */
export function effectiveParamsText(
  eff: Record<string, EffectiveParam> | null | undefined,
): string {
  if (!eff) return "";
  const parts: string[] = [];
  const tag = (p: EffectiveParam) => `(${SOURCE_ZH[p.source] ?? p.source})`;
  const mode = eff.processing_mode;
  if (mode)
    parts.push(
      `${MODE_ZH[String(mode.value)] ?? String(mode.value)}${tag(mode)}`,
    );
  const profit = eff.profit_ratio;
  if (profit && typeof profit.value === "number") {
    parts.push(`利润率 ${Math.round(profit.value * 1000) / 10}%${tag(profit)}`);
  }
  const tol = eff.tolerance;
  if (tol) parts.push(`公差 ${toleranceLabel(tol.value)}${tag(tol)}`);
  // 方案 D8:一次性费用怎么收 + 每批件数(拆开收时装夹调机按批次)
  const onetime = eff.onetime_mode;
  if (onetime)
    parts.push(
      `一次性费用${ONETIME_ZH[String(onetime.value)] ?? String(onetime.value)}${tag(onetime)}`,
    );
  const lot = eff.lot_size;
  if (lot && typeof lot.value === "number")
    parts.push(`每批 ${lot.value} 件${tag(lot)}`);
  return parts.join(" · ");
}

/** 一次性费用收法(方案 D8)→ 中文。 */
export const ONETIME_ZH: Record<string, string> = {
  per_order: "拆开收(编程每个图号一次、装夹调机按批次)",
  threshold: "按摊薄阈值(同单件报价)",
};

/** 批量改的结果 → 一句话(跳过的行逐个写原因,不静默)。 */
/**
 * 勾选的行里,有几行的工艺是按读图填的、要人核对(能点「工艺核对无误」),其中几行 3D 显示可能要多轴
 * (点之前要再确认一次:这几行很可能报低了)。判定用后端给的 `process_review`。
 */
export function reviewOf(
  lines: readonly ProjectLine[],
  picked: ReadonlySet<string>,
): { reviewable: number; risky: number; lineIds: string[] } {
  const chosen = lines.filter((l) => picked.has(l.id) && processNeedsReview(l));
  return {
    reviewable: chosen.length,
    risky: chosen.filter((l) => l.process_review === "blocked").length,
    // 只发要核对的那几行:别的行发过去也是逐行跳过,结果里一长串「不用核对」只会把要看的淹掉
    lineIds: chosen.map((l) => l.id),
  };
}

/** 「工艺核对无误」的结果 → 一句话(跳过的逐行写原因,不静默)。 */
export function reviewResultText(res: {
  reviewed: number;
  skipped: { name: string; reason: string }[];
}): string {
  const head =
    res.reviewed > 0
      ? `已核对 ${res.reviewed} 行的工艺(工艺不变、没有重算)`
      : "没有行要核对";
  if (res.skipped.length === 0) return head;
  const why = res.skipped
    .map((s) => `${s.name || "(未知行)"}:${s.reason}`)
    .join(";");
  return `${head};跳过 ${res.skipped.length} 行 —— ${why}`;
}

export function batchResultText(res: {
  updated: number;
  skipped: { name: string; reason: string }[];
}): string {
  const head =
    res.updated > 0 ? `已改 ${res.updated} 行,正在重新算价` : "没有行被修改";
  if (res.skipped.length === 0) return head;
  const why = res.skipped
    .map((s) => `${s.name || "(未知行)"}:${s.reason}`)
    .join(";");
  return `${head};跳过 ${res.skipped.length} 行 —— ${why}`;
}

/**
 * 读不出来、要人定的项按字段汇总(计划 M4 第 3 条):一组一句话 + 这组的行,工作台一键勾选交给批量改。
 *
 * - 工艺(待人定):读图判断了但没填进草稿(车削 / 钣金、没读出、目录里没有那道工艺,或旧记录),且人还没定过
 * - 工艺(请核对):按读图填进草稿了、人还没定过、没有 3D 核对通过(`processNeedsReview`)。价已经算出来了,
 *   但那个工艺没人把过关 —— 其中「3D 显示可能要多轴」的很可能报低了,单独一句、排在最前面
 * - 材料:图纸读到了但不确定(并列 / 只是相近 / 与重量对不上),且草稿里的材料不是人或项目参数给的 ——
 *   也就是草稿现在没有材料
 * - 表处理写「见附件」:附件没随文件提供,已按图纸上的字面(如喷漆)计价;人在批量改里统一定了表处理
 *   (行上表处理来源是「人定」)之后就不再问
 *
 * 只看已建草稿、勾选计价的行。
 */
export interface PendingDecision {
  key:
    | "process"
    | "process_blocked"
    | "process_review"
    | "material"
    | "attachment";
  /** 这句话前面的两三个字:「待人定」(默认 · 不定就没有价)/「请核对」(已经按读图填了,价算出来了) */
  label?: string;
  text: string;
  lineIds: string[];
  /** 能交给批量条一次定完(工艺 / 材料 / 表处理) */
  selectable: boolean;
}

export function pendingDecisions(
  lines: readonly ProjectLine[],
): PendingDecision[] {
  const live = lines.filter(
    (l) => QUOTABLE_KINDS.has(l.kind) && l.included && l.quote_id != null,
  );
  const process = live.filter((l) => {
    const p = l.inference?.process;
    return p != null && !p.adopted && l.applied?.process !== "user";
  });
  const material = live.filter((l) => {
    const m = l.inference?.material;
    return m != null && !m.sure && l.applied?.material == null;
  });
  // 表处理写「…见附件」:附件没随文件提供,已按图纸上的字面(如喷漆)计价,规格要人统一确认一次
  const attachment = live.filter(
    (l) =>
      (l.inference?.surfaces?.notes ?? []).some((n) => n.includes("附件")) &&
      l.applied?.surfaces !== "user",
  );
  const out: PendingDecision[] = [];
  const review = live.filter(processNeedsReview);
  const blockedReview = review.filter((l) => l.process_review === "blocked");
  if (blockedReview.length)
    out.push({
      key: "process_blocked",
      label: "请先核对",
      text:
        `${blockedReview.length} 行工艺按读图填了三轴,但 3D 显示可能要多轴 —— 很可能报低了。` +
        "不对的勾选后在批量改里换成四轴 / 五轴;看过确实是三轴的点「工艺核对无误」",
      lineIds: blockedReview.map((l) => l.id),
      selectable: true,
    });
  if (process.length) {
    // 读图说了三轴、却没按读图填的两种(§5.18)单独点出来:不说的话,人会以为读图没判断
    const judged = (check: string) =>
      process.filter((l) => l.inference?.process?.check === check).length;
    const blocked = judged("blocked");
    const drawingOnly = judged("no_3d");
    out.push({
      key: "process",
      text:
        `${process.length} 行工艺要人选(读图没读出、认为是车削 / 钣金,或读图判的那道工艺没能填上` +
        (blocked ? `;其中 ${blocked} 行读图说三轴,但 3D 显示可能要多轴` : "") +
        (drawingOnly
          ? `;${drawingOnly} 行读图说三轴,但只有图纸、没法用 3D 核对`
          : "") +
        ")",
      lineIds: process.map((l) => l.id),
      selectable: true,
    });
  }
  const otherReview = review.filter((l) => l.process_review !== "blocked");
  if (otherReview.length) {
    const because = (why: ProcessReview) =>
      otherReview.filter((l) => l.process_review === why).length;
    const multi = because("multi_axis");
    const drawingOnly = because("no_3d");
    const byClass = because("process_class");
    const facts = [
      drawingOnly ? `${drawingOnly} 行只有图纸` : "",
      multi ? `${multi} 行读图判的是多轴,分不准是四轴、3+2 还是五轴联动` : "",
      byClass
        ? `${byClass} 行读图只分得出车削 / 钣金,填的是这一类最常用的那道`
        : "",
    ].filter(Boolean);
    out.push({
      key: "process_review",
      label: "请核对",
      text:
        `${otherReview.length} 行工艺是按读图预填的,没有 3D 核对过` +
        (facts.length ? `(${facts.join(";")})` : "") +
        " —— 价已经按它算了。勾上以后:不对的在批量改里换,没问题的点「工艺核对无误」",
      lineIds: otherReview.map((l) => l.id),
      selectable: true,
    });
  }
  if (material.length)
    out.push({
      key: "material",
      text: `${material.length} 行材料没读准(并列 / 只是相近 / 与重量对不上)`,
      lineIds: material.map((l) => l.id),
      selectable: true,
    });
  if (attachment.length)
    out.push({
      key: "attachment",
      text:
        `${attachment.length} 行表处理写「见附件」,附件没随文件提供 —— ` +
        "已按图纸上写的(如喷漆)计价,具体规格请在批量改里统一定一次",
      lineIds: attachment.map((l) => l.id),
      selectable: true,
    });
  return out;
}

/**
 * 零散多件按名称配对的结果 → 给人看的几句话(计划 §5.17)。配不上的逐类列出,
 * 并说清怎么办:改名后重新建,或按「只有模型 / 只有图」照常报。
 */
export function loosePairingLines(
  p: LoosePairing,
  lines: readonly ProjectLine[] = [],
): string[] {
  // 配对结果是建项目那一刻存下的;之后补传配上的(计划 §5.30)不能还说「没配到图纸」
  const filled = new Set(lines.filter(hasDrawing).map((l) => l.name));
  const stillMissing = p.cad_only.filter((n) => !filled.has(n));
  const added = p.cad_only.length - stillMissing.length;
  const out = [
    `按名称配上 ${p.paired} 对` + (added ? ` · 之后补传配上 ${added} 件` : ""),
  ];
  if (stillMissing.length)
    out.push(`只有 3D、没配到图纸:${stillMissing.join("、")}`);
  if (p.drawing_only.length)
    out.push(
      `只有图纸、没有 3D(已按「只有图」建行):${p.drawing_only.join("、")}`,
    );
  if (p.duplicates.length)
    out.push(
      `同名重复、没法按名称配:${p.duplicates.join("、")}(请改名后重新建,或按只有模型 / 只有图报)`,
    );
  if (p.unnamed_pages)
    out.push(`${p.unnamed_pages} 页认不出零件名(总装图 / 明细表),没有建行`);
  return out;
}

/* ── 补传图纸的结果(计划 §5.17 补完)──────────────────────────────────── */

/** 补传的一份 PDF 配成了什么样 → 一句话。按标题栏配页的照原来「重新配图纸」那样说清逐页统计。 */
export function drawingFileText(f: DrawingFileOutcome): string {
  if (f.how === "文件名" && f.lines.length)
    return `${f.filename} → ${f.lines.join("、")}(按文件名,整份)`;
  if (f.how !== "标题栏") return `${f.filename}:${f.note}`;
  const bits = [
    `${f.filename}:${f.total_pages} 页 · 配上 ${f.matched_pages} 页(${f.lines.join("、")})`,
  ];
  if (f.created_lines)
    bits.push(
      `另建 ${f.created_lines} 行(图纸里有、3D 清单里没有,每套用量请核对)`,
    );
  if (f.unnamed_pages)
    bits.push(
      `${f.unnamed_pages} 页认不出零件名(可能是总装图或明细表),没有建行`,
    );
  if (f.ambiguous)
    bits.push(
      `${f.ambiguous} 个零件的名字近似到同一页,没有自动配(请在页码栏手动配)`,
    );
  if (f.vlm_used) bits.push("这份是扫描件,走了图像识别");
  return bits.join(" · ");
}

/** 补传的汇总一句话:配到几行、换掉几行、几行人工配过没换;已建草稿的行怎么更新。 */
export function addDrawingsText(r: AddDrawingFilesResult): string {
  const parts = [`${r.files.length} 份图纸 · 配到 ${r.paired_lines} 行`];
  if (r.replaced) parts.push(`其中 ${r.replaced} 行换掉了原来的图纸`);
  if (r.kept_manual) parts.push(`${r.kept_manual} 行人工配过图纸,没有换`);
  const head = parts.join(" · ");
  // 不点名「开始报价」:只剩失败行时按钮叫「重试失败的 N 行」(见 retryFailedCount)
  return r.paired_lines > 0
    ? `${head}。已建草稿的行再点清单下方的按钮重算时会换成新图纸,人改过的值不动`
    : head;
}

/**
 * 要人指给某一行的图纸:按文件名、按标题栏都没配上的;同名重复的;以及按文件名对上了、
 * 但那一行**人工配过图纸**、后端没有换的(`how = 文件名` 却没配到任何行)—— 想换就在这里整份配给它,
 * 走原来的手动指定接口。不然「换一份清楚的图纸」对人工配过的行走不通。
 */
export function filesToAssign(r: AddDrawingFilesResult): DrawingFileOutcome[] {
  return r.files.filter(
    (f) =>
      f.how === "没配上" ||
      f.how === "同名重复" ||
      (f.how === "文件名" && f.lines.length === 0),
  );
}

/* ── 工作台排序与筛选(计划 §5.14 补完)──────────────────────────────────── */

/** 工作台可排序的列。「占比」和「小计」同序,共用 `subtotal`。 */
export type WorkbenchSortKey = "name" | "quantity" | "subtotal";

export interface WorkbenchSort {
  key: WorkbenchSortKey;
  dir: "asc" | "desc";
}

/** 每列第一下的方向:金额、件数先看大的;名称先顺排。 */
const FIRST_DIR: Record<WorkbenchSortKey, WorkbenchSort["dir"]> = {
  name: "asc",
  quantity: "desc",
  subtotal: "desc",
};

/**
 * 点一下表头之后的排序。同一列:默认方向 → 反向 → 回到清单顺序(null);
 * 换一列从那一列的默认方向开始。
 */
export function nextSort(
  cur: WorkbenchSort | null,
  key: WorkbenchSortKey,
): WorkbenchSort | null {
  const first = FIRST_DIR[key];
  if (cur == null || cur.key !== key) return { key, dir: first };
  if (cur.dir === first) return { key, dir: first === "asc" ? "desc" : "asc" };
  return null;
}

const byName = new Intl.Collator("zh-CN", {
  numeric: true,
  sensitivity: "base",
});

/**
 * 按所选列排工作台的行(返回新数组,不动传入的)。同值按清单原顺序,保证稳定。
 *
 * 按小计排时,**没算价的行不论升降都排最后** —— 表上显示「——」不是 0,
 * 排到「最便宜」的前面会让人以为它最便宜。`priceOf` 必须是表上显示的同一个数。
 */
export function sortWorkbench<T extends ProjectLine>(
  lines: readonly T[],
  sort: WorkbenchSort | null,
  priceOf: (ln: T) => number | undefined,
): T[] {
  const out = [...lines];
  if (sort == null) return out;
  const sign = sort.dir === "asc" ? 1 : -1;
  // Array.prototype.sort 是稳定排序:比较结果为 0 的保持清单顺序
  return out.sort((a, b) => {
    if (sort.key === "name") return sign * byName.compare(a.name, b.name);
    if (sort.key === "quantity") return sign * (a.quantity - b.quantity);
    const pa = priceOf(a);
    const pb = priceOf(b);
    if (pa == null || pb == null)
      return (pa == null ? 1 : 0) - (pb == null ? 1 : 0);
    return sign * (pa - pb);
  });
}

/**
 * 工作台筛选的全部键 —— 筛选定义和回程快照白名单(`projectJourneyView`)共用这一份,
 * 不再各写一张表(两处各写,加了新筛选忘了改白名单,整份回程就被判非法丢掉)。
 */
export const WORKBENCH_FILTER_KEYS = [
  "process_blocked",
  "process",
  "process_review",
  "material",
  "attachment",
  "failed",
  "drawing",
  "no_3d",
  "extrapolated",
  "no_draft",
  "missing_inputs",
  "readiness",
  "draft_behind",
] as const satisfies readonly (
  | PendingDecision["key"]
  | "failed"
  | "drawing"
  | "no_3d"
  | "extrapolated"
  | "no_draft"
  | "missing_inputs"
  | "readiness"
  | "draft_behind"
)[];

export type WorkbenchFilterKey = (typeof WORKBENCH_FILTER_KEYS)[number];

/** 一个「只看这类行」的筛选:名字 + 属于这一类的行。 */
export interface WorkbenchFilter {
  key: WorkbenchFilterKey;
  label: string;
  lineIds: string[];
}

const DECISION_LABEL: Record<PendingDecision["key"], string> = {
  process_blocked: "工艺先核对(3D 可能多轴)",
  process: "工艺要人选",
  process_review: "工艺待核对",
  material: "材料没读准",
  attachment: "表处理见附件",
};

/**
 * 工作台能按哪几类筛(只给有行的类,顺序固定)。
 *
 * 工艺 / 材料 / 见附件三类**直接用 `pendingDecisions` 的结果** —— 与表格上方「待人定」
 * 同一份判定,不另写一遍(两处各判一遍,改坏一处另一处兜住,测试照绿)。
 * 图纸待核对 / 未建草稿与行徽章同一条判定;标外推与「外推」标签同一条(「样本少」不算)。
 */
export function workbenchFilters(
  lines: readonly ProjectLine[],
  calibration: ProjectCalibration | null | undefined,
): WorkbenchFilter[] {
  const out: WorkbenchFilter[] = pendingDecisions(lines).map((d) => ({
    key: d.key,
    label: DECISION_LABEL[d.key],
    lineIds: d.lineIds,
  }));
  const add = (
    key: WorkbenchFilterKey,
    label: string,
    hit: (ln: ProjectLine) => boolean,
  ) => {
    const ids = lines.filter(hit).map((l) => l.id);
    if (ids.length) out.push({ key, label, lineIds: ids });
  };
  add("failed", "识别失败", lineRecognitionFailed);
  add("drawing", "图纸待核对", drawingNeedsConfirm);
  add("no_3d", "没有 3D", lacks3d);
  add(
    "extrapolated",
    "标外推",
    (ln) => calibrationChip(ln.id, calibration)?.text === "外推",
  );
  add("no_draft", "未建草稿", lacksDraft);
  add("missing_inputs", "缺必填", lacksInputs);
  add(
    "readiness",
    "挡出价",
    (line) => projectBlockingGroups([line]).length > 0,
  );
  add("draft_behind", "草稿待更新", (line) => line.draft_behind === true);
  return out;
}

/* ── 步骤名与步骤状态(总纲 §5.5 P2 · §5.7 E3)──────────────────────────── */

/** 项目页三步:说用户要做的事。 */
export const PROJECT_STEP_LABELS = {
  checklist: "确认零件清单",
  workbench: "逐行核对",
  summary: "出报价单",
} as const;

export interface ProjectStepStatus {
  /** 要计价的勾选行都建了草稿(没有要计价的行时为 false) */
  checklistDone: boolean;
  /** 逐行核对还要处理的行数:待人定 / 请核对 ∪ 识别失败 ∪ 缺必填,按行去重 */
  workbenchPending: number;
  /**
   * 出报价单:算过(汇总里有当前套数这一档)就按汇总 —— 有行没算进来即未完整;
   * 还没算(或没有这一档)就按行 —— 要计价的勾选行有一行没出价即未完整。
   */
  summaryIncomplete: boolean;
}

/** 三步各自的状态(只从项目详情和已算的汇总派生,不发请求)。 */
export function projectStepStatus(
  lines: readonly ProjectLine[],
  summary: ProjectSummary | null,
  sets: number,
): ProjectStepStatus {
  const live = lines.filter((l) => QUOTABLE_KINDS.has(l.kind) && l.included);
  const pending = new Set<string>([
    ...pendingDecisions(lines).flatMap((d) => d.lineIds),
    ...projectBlockingGroups(lines).flatMap((group) => group.lineIds),
  ]);
  for (const l of live)
    if (lineRecognitionFailed(l) || lacksInputs(l)) pending.add(l.id);
  // 汇总只在第 3 步点「算价」才有,任何改动都会清掉:没算过时按行判断,不然这一格几乎一直亮着(复审)
  const computed = summary != null && summary.totals[String(sets)] != null;
  return {
    checklistDone: live.length > 0 && live.every((l) => l.quote_id != null),
    workbenchPending: pending.size,
    summaryIncomplete: computed
      ? totalDisplay(summary, sets).kind === "hidden"
      : live.length === 0 || !live.every(linePriced),
  };
}

/** 全部项目入口共用的固定路由，不接收 URL 或查询参数。 */
export function quoteProjectPath(id: string): string {
  return `/app/quickquote/projects/${id}`;
}
export function isProjectId(id: string | undefined): id is string {
  return (
    !!id &&
    /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(id)
  );
}
export function initialProjectStep(
  readiness: import("@/api/quoteProjects").ProjectReadiness | null | undefined,
): "checklist" | "workbench" | "summary" {
  return readiness?.next_step ?? "checklist";
}
export interface ProjectBlockingGroup {
  key: string;
  code: string;
  label: string;
  lineIds: string[];
}
/** 只消费后端逐行条目与落后标记；同名行仍分别保留身份。 */
export function projectBlockingGroups(
  lines: readonly ProjectLine[],
): ProjectBlockingGroup[] {
  const groups = new Map<string, ProjectBlockingGroup>();
  const add = (code: string, label: string, id: string) => {
    const group = groups.get(code) ?? {
      key: `readiness:${code}`,
      code,
      label,
      lineIds: [],
    };
    if (!group.lineIds.includes(id)) group.lineIds.push(id);
    groups.set(code, group);
  };
  for (const line of lines) {
    if (!line.id || !line.included || !QUOTABLE_KINDS.has(line.kind)) continue;
    for (const item of line.readiness?.items ?? []) {
      if (
        typeof item.code === "string" &&
        typeof item.message === "string" &&
        Array.isArray(item.blocks) &&
        item.blocks.length > 0
      )
        add(item.code, item.message, line.id);
    }
    if (line.draft_behind)
      add("DRAFT_BEHIND", "清单有变化，草稿待更新", line.id);
  }
  return [...groups.values()];
}
const PREFILL_PLAN_LABELS: Record<string, string> = {
  new: "新建",
  retry_failed: "失败",
  drawing_changed: "换图纸",
  cad_changed: "换3D",
  qty_changed: "数量变化",
  selections_changed: "选型变化",
  onetime_changed: "一次性费用变化",
  params_skipped: "补应用参数",
  unit_prices_skipped: "补应用价格",
};
export function prefillPlanText(
  plan: Record<string, number> | null | undefined,
): string | null {
  if (!plan) return null;
  const parts = Object.entries(PREFILL_PLAN_LABELS).flatMap(([key, label]) =>
    typeof plan[key] === "number" && plan[key] > 0
      ? [`${label}${plan[key]}行`]
      : [],
  );
  return parts.length ? `重算：${parts.join("、")}` : null;
}
export function unitPriceSourceLabel(meta: unknown): string {
  const source =
    meta && typeof meta === "object" && "source" in meta
      ? meta.source
      : undefined;
  return source === "ai"
    ? "AI询价"
    : source === "human"
      ? "人工填写"
      : source === "pin_snapshot"
        ? "本厂钉价快照"
        : "历史来源缺失";
}

/** 按后端给出的落后原因说明本次动作，不把“复用”当作重新识别。 */
export function projectRecomputeLabel(
  lines: readonly ProjectLine[],
): string | null {
  const live = lines.filter(
    (line) => line.included && QUOTABLE_KINDS.has(line.kind),
  );
  const behind = live.filter((line) => line.draft_behind);
  if (!behind.length) return null;
  const fresh = live.filter((line) => !line.quote_id).length;
  if (fresh) return `开始报价：新建${fresh}行、更新${behind.length}行`;
  const labels: Record<string, string> = {
    drawing: "换图纸",
    cad: "换3D",
    qty: "数量变化",
    prefill_failed: "失败",
    selections: "选型变化",
    onetime_policy: "费用收法变化",
    params_skipped: "补应用参数",
    unit_prices_skipped: "补应用价格",
  };
  const counts = new Map<string, number>();
  for (const line of behind)
    for (const reason of new Set(line.draft_behind_reasons ?? []))
      if (labels[reason]) counts.set(reason, (counts.get(reason) ?? 0) + 1);
  const parts = [...counts].map(
    ([reason, count]) => `${labels[reason]}${count}行`,
  );
  return `重算${behind.length}行${parts.length ? `：${parts.slice(0, 2).join("、")}${parts.length > 2 ? "等" : ""}` : "（草稿有变化）"}`;
}
